import type { TeaserBaseProps } from '@bonniernews/dn-design-system-web/assets/types/teaser-props.ts'

import { formatClassString } from '@bonniernews/dn-design-system-web/helpers/formatClassString.ts'

import { Button } from '@bonniernews/dn-design-system-web/components/button/button.tsx'
import {
  GroupHeader,
  GroupHeaderType,
} from '@bonniernews/dn-design-system-web/components/group-header/group-header.tsx'
import { TeaserCard } from '@bonniernews/dn-design-system-web/components/teaser-card/teaser-card.tsx'
import {
  TeaserSwipeCard,
  TeaserSwipeCardProps,
} from '@bonniernews/dn-design-system-web/components/teaser-swipe-card/teaser-swipe-card.tsx'

interface TeaserListSwipeProps extends Omit<TeaserBaseProps, 'targetLink'> {
  /** The header of the teaser list */
  title: string
  areaType?: 'main' | 'bauta'
  /** Target URL for the header */
  titleHref?: string
  /** Determines where the scroll arrows are rendered */
  arrowPosition?: 'floating' | 'header'
  /** A list of arguments to TeaserSwipeCard */
  teasers: TeaserSwipeCardProps[]
  /** Type of group header */
  groupHeaderType?: GroupHeaderType
  /** Text in toggle button */
  groupHeaderToggleText?: string
  /** Text in toggle button when selected */
  groupHeaderToggleSelectedText?: string
  /** Is toggle button selected or not */
  groupHeaderToggleSelected?: boolean
  /** Class names for toggle button */
  groupHeaderToggleClassNames?: string
  /** Attributes for toggle button. Ex. { "data-entity-id": "dn.epi.434" } */
  groupHeaderToggleAttributes?: Record<string, string>
  /** Used to include or exclude elements based on channel */
  channel?: 'mobile' | 'tablet' | 'desktop'
}

/**
 * - GitHub: [BonnierNews/dn-design-system/web/src/components/teaser-list-swipe](https://github.com/BonnierNews/dn-design-system/tree/main/web/src/components/teaser-list-swipe)
 * - Storybook: [TeaserListSwipe](https://designsystem.dn.se/?path=/docs/section-teaserlistswipe--docs)
 *
 * The component will not include styling by itself. Make sure to include the right styles for the component. See example below:
 * `@use '@bonniernews/dn-design-system-web/components/teaser-list-swipe/teaser-list-swipe.scss'`
 */
export const TeaserListSwipe = ({
  title,
  titleHref,
  areaType,
  arrowPosition = 'header',
  teasers = [],
  groupHeaderType,
  groupHeaderToggleText,
  groupHeaderToggleSelectedText,
  groupHeaderToggleSelected = false,
  groupHeaderToggleClassNames,
  groupHeaderToggleAttributes = {},
  classNames,
  attributes = {},
  channel,
}: TeaserListSwipeProps) => {
  const componentClassName = 'ds-teaser'
  const classes = formatClassString(['ds-teaser--list-swipe', classNames])
  const headerType = groupHeaderType || (arrowPosition === 'header' && 'arrows') || 'link'

  return (
    <TeaserCard areaType={areaType} classNames={classes} attributes={attributes}>
      <GroupHeader
        title={title}
        href={titleHref}
        type={headerType}
        linkText='Visa alla'
        toggleText={groupHeaderToggleText}
        toggleSelectedText={groupHeaderToggleSelectedText}
        toggleSelected={groupHeaderToggleSelected}
        toggleClassNames={groupHeaderToggleClassNames}
        toggleAttributes={groupHeaderToggleAttributes}
        channel={channel}
      />
      <div className={`${componentClassName}__carousel`}>
        {teasers.map((teaser, index) => (
          <TeaserSwipeCard key={index} {...teaser} />
        ))}
      </div>

      {arrowPosition === 'floating' && channel === 'desktop' && (
        <>
          <Button
            iconName='arrow_back'
            variant='staticWhite'
            size='sm'
            isIconButton={true}
            attributes={{ 'aria-label': 'Föregående' }}
            classNames={`${componentClassName}__swipe-arrow ${componentClassName}__swipe-arrow--back`}
          />
          <Button
            iconName='arrow_forward'
            variant='staticWhite'
            size='sm'
            isIconButton={true}
            attributes={{ 'aria-label': 'Nästa' }}
            classNames={`${componentClassName}__swipe-arrow ${componentClassName}__swipe-arrow--forward`}
          />
        </>
      )}
    </TeaserCard>
  )
}
